import {
  Alert,
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  Tab,
  Tabs,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle,
  SwapVert,
  People,
  Message,
  TrendingUp,
  SyncAlt,
} from "@mui/icons-material";
import React, { useState, useMemo, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import RefreshIcon from "@mui/icons-material/Refresh";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

const COLORS = ["#378ADD", "#1D9E75", "#D85A30", "#7F77DD", "#D4537E"];

const fmt = (n) =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toLocaleString();

const shortName = (name) => (name.length > 14 ? name.slice(0, 13) + "…" : name);

const SummaryCard = ({ label, value, icon }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 2, background: "background.default" }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
      {icon}
      <Typography variant="h5" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: 160 }}>
      <Typography variant="caption" fontWeight={500} display="block" mb={0.5}>
        {label}
      </Typography>
      {payload.map((p) => (
        <Box
          key={p.dataKey}
          sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
        >
          <Typography variant="caption" color="text.secondary">
            {p.name}
          </Typography>
          <Typography variant="caption" fontWeight={500}>
            {p.value.toLocaleString()}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

const RcsClientsStats = () => {
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/rcs/client-leads-stats`
  );
  const [tab, setTab] = useState("leads");
  const [sortDir, setSortDir] = useState("desc");
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(
        `${API_ENDPOINT}/root/refresh-rcs-stats`,
        getHeader()
      );
      refetch();
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setRefreshing(false);
    }
  }, []);
  const sorted = useMemo(() => {
    if (!data) return [];
    const key = tab === "leads" ? "totalLeads" : "totalMessages";
    return [...data].sort((a, b) =>
      sortDir === "desc" ? b[key] - a[key] : a[key] - b[key]
    );
  }, [data, tab, sortDir]);

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  const totalLeads = data.reduce((s, d) => s + d.totalLeads, 0);
  const totalMessages = data.reduce((s, d) => s + d.totalMessages, 0);
  const convRate =
    totalMessages > 0
      ? ((totalLeads / totalMessages) * 100).toFixed(2)
      : "0.00";

  const key = tab === "leads" ? "totalLeads" : "totalMessages";
  const maxVal = Math.max(...data.map((d) => d[key]));
  const totalVal = data.reduce((s, d) => s + d[key], 0);
  const syncTime = data[0]?.lastSyncedAt
    ? new Date(data[0].lastSyncedAt).toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";
  const chartData = data.map((d, i) => ({
    name: shortName(d.customerId),
    Messages: d.totalMessages,
    Leads: d.totalLeads,
    color: COLORS[i % COLORS.length],
  }));

  return (
    <Container sx={{ mt: 3, mb: 6 }}>
      <BackBtn />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={500}>
            RCS client stats
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Synced today at {syncTime} IST
          </Typography>
          <Tooltip title="Refresh">
            {refreshing ? (
              <CircularProgress />
            ) : (
              <IconButton size="small" onClick={handleRefresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
          </Tooltip>
        </Box>
        <Chip
          icon={<CheckCircle sx={{ fontSize: 14 }} />}
          label="Live"
          color="success"
          size="small"
        />
      </Box>

      {/* Summary cards */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[
          {
            label: "Total clients",
            value: data.length,
            icon: <People fontSize="small" color="action" />,
          },
          {
            label: "Total leads",
            value: totalLeads.toLocaleString(),
            icon: <TrendingUp fontSize="small" color="primary" />,
          },
          {
            label: "Total messages",
            value: fmt(totalMessages),
            icon: <Message fontSize="small" color="action" />,
          },
          {
            label: "Conversion rate",
            value: `${convRate}%`,
            icon: <SyncAlt fontSize="small" color="action" />,
          },
        ].map((m) => (
          <Grid item xs={6} sm={3} key={m.label}>
            <SummaryCard {...m} />
          </Grid>
        ))}
      </Grid>

      {/* Ranked list */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pt: 1,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 36 }}
          >
            <Tab label="Leads" value="leads" sx={{ minHeight: 36, py: 0.5 }} />
            <Tab
              label="Messages"
              value="messages"
              sx={{ minHeight: 36, py: 0.5 }}
            />
          </Tabs>
          <Tooltip
            title={`Sort ${sortDir === "desc" ? "ascending" : "descending"}`}
          >
            <IconButton
              size="small"
              onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
            >
              <SwapVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ px: 2, pb: 1 }}>
          {sorted.map((d, i) => {
            const val = d[key];
            const pct =
              totalVal > 0 ? ((val / totalVal) * 100).toFixed(1) : "0.0";
            const barPct = maxVal > 0 ? (val / maxVal) * 100 : 0;
            const color = COLORS[i % COLORS.length];
            const initials = d.customerId
              .split(" ")
              .slice(0, 2)
              .map((w) => w[0])
              .join("")
              .toUpperCase();
            return (
              <Box
                key={d._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1,
                  borderBottom: i < sorted.length - 1 ? "0.5px solid" : "none",
                  borderColor: "divider",
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 11,
                    bgcolor: color + "22",
                    color,
                  }}
                >
                  {initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {d.customerId}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={barPct}
                    sx={{
                      mt: 0.5,
                      height: 5,
                      borderRadius: 99,
                      bgcolor: "action.hover",
                      "& .MuiLinearProgress-bar": { bgcolor: color },
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{ minWidth: 56, textAlign: "right" }}
                >
                  {fmt(val)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 40, textAlign: "right" }}
                >
                  {pct}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Recharts: Messages vs Leads */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mt: 2 }}>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
          Messages vs leads per client
        </Typography>

        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={chartData}
            margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
            barCategoryGap="28%"
            barGap={4}
          >
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              stroke="#e0e0e0"
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#888" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="msg"
              orientation="left"
              tick={{ fontSize: 11, fill: "#1D9E75" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => fmt(v)}
              label={{
                value: "Messages",
                angle: -90,
                position: "insideLeft",
                offset: 10,
                style: { fontSize: 11, fill: "#1D9E75" },
              }}
            />
            <YAxis
              yAxisId="leads"
              orientation="right"
              tick={{ fontSize: 11, fill: "#378ADD" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => fmt(v)}
              label={{
                value: "Leads",
                angle: 90,
                position: "insideRight",
                offset: 10,
                style: { fontSize: 11, fill: "#378ADD" },
              }}
            />
            <RTooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
              iconType="square"
              iconSize={10}
            />
            <Bar
              yAxisId="msg"
              dataKey="Messages"
              fill="#1D9E75"
              radius={[3, 3, 0, 0]}
              maxBarSize={32}
            />
            <Bar
              yAxisId="leads"
              dataKey="Leads"
              fill="#378ADD"
              radius={[3, 3, 0, 0]}
              maxBarSize={32}
            />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Recharts: Leads share — RadialBar */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mt: 2 }}>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
          Lead share by client
        </Typography>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart
            layout="vertical"
            data={[...data]
              .sort((a, b) => b.totalLeads - a.totalLeads)
              .map((d, i) => ({
                name: shortName(d.customerId),
                Leads: d.totalLeads,
                fill: COLORS[i % COLORS.length],
              }))}
            margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
          >
            <CartesianGrid
              horizontal={false}
              strokeDasharray="3 3"
              stroke="#e0e0e0"
            />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: "#888" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => fmt(v)}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 11, fill: "#888" }}
              axisLine={false}
              tickLine={false}
              width={110}
            />
            <RTooltip content={<CustomTooltip />} />
            <Bar dataKey="Leads" radius={[0, 3, 3, 0]} maxBarSize={18}>
              {[...data]
                .sort((a, b) => b.totalLeads - a.totalLeads)
                .map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </Container>
  );
};

export default RcsClientsStats;
